import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ServicesPage from "@/pages/Services";
import ManagedITPage from "@/pages/ManagedIT";
import CloudSolutionsPage from "@/pages/CloudSolutions";
import ITConsultingPage from "@/pages/ITConsulting";
import AboutPage from "@/pages/About";
import ContactPage from "@/pages/Contact";
import PrivacyPolicyPage from "@/pages/PrivacyPolicy";
import CybersecurityPage from "@/pages/Cybersecurity";
import {
  Phone,
  Monitor,
  Shield,
  Cloud,
  Check,
  Clock,
  FileText,
  Users,
  Briefcase,
  Heart,
  HardHat,
  ShoppingBag,
  Factory,
  GraduationCap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="min-h-[70vh] flex items-center justify-center bg-gradient-to-b from-blue-50/30 to-white"
    >
      <div className="max-w-4xl mx-auto px-5 py-20 text-center">
        <h1
          data-testid="hero-headline"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6"
          style={{ color: "#1F2937", maxWidth: "800px", margin: "0 auto 24px" }}
        >
          Reliable IT Support for Your Growing Business
        </h1>
        <p
          data-testid="hero-subheadline"
          className="text-lg sm:text-xl mb-10 leading-relaxed"
          style={{ color: "#4A5568", maxWidth: "700px", margin: "0 auto 40px" }}
        >
          Professional IT services to keep your business secure, connected, and
          running smoothly
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={scrollToContact}
            data-testid="hero-cta-assessment"
            className="w-full sm:w-auto text-lg font-semibold px-8 py-6 rounded-lg shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: "#3B82F6" }}
          >
            Get Free IT Assessment
          </Button>
          <a
            href="tel:7783497449"
            data-testid="hero-cta-call"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-lg font-semibold px-8 py-4 rounded-lg border-2 transition-all hover:-translate-y-0.5"
            style={{
              borderColor: "#2C5F2D",
              color: "#2C5F2D",
              backgroundColor: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#2C5F2D";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#2C5F2D";
            }}
          >
            <Phone size={20} />
            Call (778) 349-7449
          </a>
        </div>
      </div>
    </section>
  );
};

// Service Cards Section
const ServiceCardsSection = () => {
  const services = [
    {
      icon: Monitor,
      title: "Managed IT Services",
      description:
        "24/7 monitoring and support to keep your business running smoothly",
      color: "#3B82F6",
    },
    {
      icon: Shield,
      title: "Cybersecurity",
      description:
        "Protect your business from ransomware, viruses, and cyber threats",
      color: "#2C5F2D",
    },
    {
      icon: Cloud,
      title: "Cloud Solutions",
      description:
        "Work from anywhere with secure cloud services and backup",
      color: "#3B82F6",
    },
  ];

  const scrollToServices = () => {
    const element = document.getElementById("services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 bg-white"
    >
      <div className="max-w-6xl mx-auto px-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              className="service-card bg-white border border-gray-200 rounded-xl p-10 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
            >
              <service.icon
                size={48}
                style={{ color: service.color }}
                className="mx-auto mb-5"
              />
              <h3
                className="text-2xl font-bold mb-3"
                style={{ color: "#1F2937" }}
              >
                {service.title}
              </h3>
              <p
                className="text-base leading-relaxed mb-5"
                style={{ color: "#4A5568" }}
              >
                {service.description}
              </p>
              <button
                onClick={scrollToServices}
                className="text-base font-semibold hover:underline"
                style={{ color: "#3B82F6" }}
              >
                Learn More →
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const reasons = [
    "Expert IT support tailored to small and medium businesses",
    "Fast response times when you need help most",
    "Transparent pricing with no hidden fees",
    "Proactive monitoring to prevent issues before they happen",
  ];

  return (
    <section
      id="about"
      data-testid="why-choose-section"
      className="py-20"
      style={{ backgroundColor: "#F9FAFB" }}
    >
      <div className="max-w-5xl mx-auto px-5">
        <h2
          data-testid="why-choose-title"
          className="text-3xl sm:text-4xl font-bold text-center mb-16"
          style={{ color: "#1F2937" }}
        >
          Why Businesses Choose A3 Technologies
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reasons.map((reason, index) => (
            <div
              key={index}
              data-testid={`reason-${index}`}
              className="flex items-start gap-4"
            >
              <Check
                size={24}
                style={{ color: "#2C5F2D", flexShrink: 0, marginTop: "2px" }}
              />
              <p
                className="text-lg leading-relaxed"
                style={{ color: "#4A5568" }}
              >
                {reason}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Our Commitment Section
const CommitmentSection = () => {
  const commitments = [
    {
      icon: Clock,
      title: "Fast Response Times",
      description:
        "Same-day response to your IT emergencies. When your business is down, we spring into action—not tomorrow, today.",
      color: "#3B82F6",
    },
    {
      icon: FileText,
      title: "Transparent Pricing",
      description:
        "No hidden fees, no surprises. You'll know exactly what you're paying for before we start any work.",
      color: "#2C5F2D",
    },
    {
      icon: Users,
      title: "Expert IT Team",
      description:
        "Certified professionals with years of experience helping businesses like yours succeed with technology.",
      color: "#3B82F6",
    },
  ];

  return (
    <section data-testid="commitment-section" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-5">
        <h2
          data-testid="commitment-title"
          className="text-3xl sm:text-4xl font-bold text-center mb-4"
          style={{ color: "#1F2937" }}
        >
          Our Promise to You
        </h2>
        <p
          data-testid="commitment-subtitle"
          className="text-lg text-center mb-16"
          style={{ color: "#4A5568" }}
        >
          What you can expect when you work with A3 Technologies
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {commitments.map((item, index) => (
            <div
              key={index}
              data-testid={`commitment-card-${index}`}
              className="bg-white border border-gray-200 rounded-xl p-10 text-center transition-all duration-300 hover:-translate-y-1"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            >
              <item.icon
                size={56}
                style={{ color: item.color }}
                className="mx-auto mb-6"
              />
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: "#1F2937" }}
              >
                {item.title}
              </h3>
              <p
                className="text-base leading-relaxed"
                style={{ color: "#4A5568" }}
              >
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  const industries = [
    {
      icon: Briefcase,
      title: "Professional Services",
      subtitle: "Accounting, Legal, Consulting",
      color: "#2C5F2D",
    },
    {
      icon: Heart,
      title: "Healthcare & Medical",
      subtitle: "Clinics, Dental, Veterinary",
      color: "#3B82F6",
    },
    {
      icon: HardHat,
      title: "Construction & Trades",
      subtitle: "Contractors, Builders, Electricians",
      color: "#2C5F2D",
    },
    {
      icon: ShoppingBag,
      title: "Retail & Hospitality",
      subtitle: "Shops, Restaurants, Hotels",
      color: "#3B82F6",
    },
    {
      icon: Factory,
      title: "Manufacturing & Distribution",
      subtitle: "Production, Logistics, Supply Chain",
      color: "#2C5F2D",
    },
    {
      icon: GraduationCap,
      title: "Non-Profits & Education",
      subtitle: "Schools, Charities, Community Orgs",
      color: "#3B82F6",
    },
  ];

  return (
    <section
      data-testid="industries-section"
      className="py-20"
      style={{ backgroundColor: "#F9FAFB" }}
    >
      <div className="max-w-6xl mx-auto px-5">
        <h2
          data-testid="industries-title"
          className="text-3xl sm:text-4xl font-bold text-center mb-4"
          style={{ color: "#1F2937" }}
        >
          Supporting Businesses Across Industries
        </h2>
        <p
          data-testid="industries-subtitle"
          className="text-lg text-center mb-16"
          style={{ color: "#4A5568" }}
        >
          No matter your industry, we have the IT expertise to keep your
          business running smoothly
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry, index) => (
            <div
              key={index}
              data-testid={`industry-card-${index}`}
              className="bg-white rounded-lg p-8 text-center"
              style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.06)" }}
            >
              <industry.icon
                size={40}
                style={{ color: industry.color }}
                className="mx-auto mb-4"
              />
              <h3
                className="text-xl font-bold mb-2"
                style={{ color: "#1F2937" }}
              >
                {industry.title}
              </h3>
              <p className="text-base" style={{ color: "#4A5568" }}>
                {industry.subtitle}
              </p>
            </div>
          ))}
        </div>
        <p
          data-testid="industries-cta"
          className="text-base italic text-center mt-10"
          style={{ color: "#4A5568" }}
        >
          Don't see your industry? We work with businesses of all types. Contact
          us to discuss your specific needs.
        </p>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      data-testid="final-cta-section"
      className="py-20"
      style={{
        background: "linear-gradient(to right, #3B82F6, #2563EB)",
      }}
    >
      <div className="max-w-4xl mx-auto px-5 text-center">
        <h2
          data-testid="final-cta-title"
          className="text-3xl sm:text-4xl font-bold text-white mb-4"
        >
          Ready to Secure Your Business?
        </h2>
        <p
          data-testid="final-cta-subtitle"
          className="text-lg mb-10"
          style={{ color: "rgba(255,255,255,0.9)" }}
        >
          Get a free IT assessment and discover how we can help
        </p>
        <Button
          onClick={scrollToContact}
          data-testid="final-cta-btn"
          className="text-lg font-bold px-10 py-6 rounded-lg shadow-md hover:bg-gray-100 transition-all"
          style={{ backgroundColor: "white", color: "#3B82F6" }}
        >
          Schedule Consultation
        </Button>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data) {
        toast.success("Thank you! We'll be in touch soon.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
        });
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-5">
        <h2
          data-testid="contact-title"
          className="text-3xl sm:text-4xl font-bold text-center mb-4"
          style={{ color: "#1F2937" }}
        >
          Get Your Free IT Assessment
        </h2>
        <p
          data-testid="contact-subtitle"
          className="text-lg text-center mb-12"
          style={{ color: "#4A5568" }}
        >
          Fill out the form below and we'll get back to you within 24 hours
        </p>
        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium mb-2"
                style={{ color: "#1F2937" }}
              >
                Full Name *
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
                data-testid="contact-name"
                className="h-12 border-gray-300 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-2"
                style={{ color: "#1F2937" }}
              >
                Email Address *
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                data-testid="contact-email"
                className="h-12 border-gray-300 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium mb-2"
                style={{ color: "#1F2937" }}
              >
                Phone Number
              </label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(778) 123-4567"
                data-testid="contact-phone"
                className="h-12 border-gray-300 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="company"
                className="block text-sm font-medium mb-2"
                style={{ color: "#1F2937" }}
              >
                Company Name
              </label>
              <Input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your Company Inc."
                data-testid="contact-company"
                className="h-12 border-gray-300 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium mb-2"
              style={{ color: "#1F2937" }}
            >
              How can we help? *
            </label>
            <Textarea
              id="message"
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your IT needs..."
              data-testid="contact-message"
              className="min-h-[120px] border-gray-300 focus:border-blue-500"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="w-full text-lg font-semibold py-6 rounded-lg transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: "#3B82F6" }}
          >
            {loading ? "Sending..." : "Request Free Assessment"}
          </Button>
        </form>
      </div>
    </section>
  );
};

// Scroll to hash handler component
const ScrollToHash = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  return null;
};

// Main Home Page
const Home = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header scrolled={scrolled} />
      <main>
        <HeroSection />
        <ServiceCardsSection />
        <WhyChooseUsSection />
        <CommitmentSection />
        <IndustriesSection />
        <FinalCTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToHash />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/managed-it" element={<ManagedITPage />} />
          <Route path="/services/cloud-solutions" element={<CloudSolutionsPage />} />
          <Route path="/services/it-consulting" element={<ITConsultingPage />} />
          <Route path="/services/cybersecurity" element={<CybersecurityPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/services/*" element={<ServicesPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
